import * as am5 from '@amcharts/amcharts5';
import { IAnnotatorEvents, IAnnotatorPrivate, IAnnotatorSettings } from '@amcharts/amcharts5/.internal/plugins/exporting/Annotator';
import * as $utils from '@amcharts/amcharts5/.internal/core/util/Utils';
import * as $object from '@amcharts/amcharts5/.internal/core/util/Object';
import { BehaviorSubject, catchError, filter, firstValueFrom, of, Subject, take, timeout } from 'rxjs';
import { ChartAnnotationMarkerTypesEnum } from '../../Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';


export class Am5ChartAnnotator extends am5.Entity {

    public static className: string = 'Annotator';
    public static classNames: Array<string> = am5.Entity.classNames.concat([Am5ChartAnnotator.className]);

    declare public _settings: IAnnotatorSettings;
    declare public _privateSettings: IAnnotatorPrivate;
    declare public _events: IAnnotatorEvents;

    private _container?: am5.Container;
    private _picture?: am5.Picture;
    private _skipRender?: boolean = false;

    private _markerArea$ = new BehaviorSubject<any>(null);
    private _markerAreaConstructionStarted = false;

    containerWidth: number = 0;
    containerHeight: number = 0;
    plotTop: number = 0;
    plotBottom: number = 0;
    plotLeft: number = 0;
    plotRight: number = 0;

    // True only for the brief window between kicking off a bake (calling markerArea.renderState()) and its 'render' event actually firing
    private _baking = false;

    // Emits once whenever a bake finishes
    private readonly bakeCompleted$ = new Subject<void>();

    // Set when a markerState change arrives while a bake is already in flight
    private _rebakeNeeded = false;
    private _bakingMarkerState: any;

    // Last geometry actually applied via applyContainerGeometry(), so repeat calls with the same
    // values can skip re-baking
    private _appliedGeometry: [number, number, number, number, number, number] | null = null;

    protected _afterNew() {
        super._afterNew();
        this._setRawDefault('layer', 1000);
        this._root.addDisposer(this);
    }

    public _beforeChanged() {
        super._beforeChanged();

        if (this.isDirty('markerState')) {
            this._renderState();
        }
    }

    // Resolves once nothing is pending anymore - no bake in flight and none queued behind it
    public async waitForBake(timeoutMs: number = 2000): Promise<void> {
        const deadline = performance.now() + timeoutMs;
        for (;;) {
            if (this.isDirty('markerState')) {
                this._renderState();
            }
            if (!this._baking && !this._rebakeNeeded) {
                return;
            }
            const remaining = deadline - performance.now();
            if (remaining <= 0) {
                return;
            }
            await firstValueFrom(this.bakeCompleted$.pipe(
                take(1),
                timeout(remaining),
                catchError(() => of(undefined))
            ));
        }
    }

    /**
     * Triggers annotation mode on the chart. This will display UI toolbars and
     * disable all interactions on the chart itself.
     */
    public async open() {
        this.applyContainerGeometry();

        // Delay this so that it's not knocked off by closing of the ExportingMenu
        this.setTimeout(() => {
            this._root._renderer.interactionsEnabled = false;
        }, 100);

        const markerArea = await this.getMarkerArea();
        markerArea.show();
        this._picture!.hide(0);
        if (this.get('markerState')) {
            markerArea.restoreState(this.get('markerState'));
        }
    }

    public _renderState(): void {
        if (this.isDisposed()) {
            return;
        }
        if (this._baking) {
            if (this.get('markerState') !== this._bakingMarkerState) {
                this._rebakeNeeded = true;
            }
            return;
        }
        if (!this.get('markerState')) {
            return;
        }
        this._baking = true;
        this._bakingMarkerState = this.get('markerState');
        this.getMarkerArea().then(markerArea => {
            if (this.isDisposed()) {
                this._baking = false;
                return;
            }
            if (this.get('markerState')) {
                this._skipRender = true;
                markerArea.renderState(this.get('markerState'));
            } else {
                this._baking = false;
            }
        });
    }

    /**
     * Exists from annotation mode. All annotations remain visible on the chart.
     */
    public async close() {
        const markerArea = await this.getMarkerArea();
        markerArea.close();
    }

    /**
     * Exits from annotation mode. Any changes made during last session of the
     * annotation editing are cancelled.
     */
    public async cancel() {
        this._root._renderer.interactionsEnabled = true;
        const markerArea = await this.getMarkerArea();
        this._picture.show(0);
        markerArea.close();
    }

    /**
     * All annotations are removed.
     */
    public clear() {
        this.set('markerState', undefined);
        if (this._picture) {
            this._picture.set('src', '');
        }
    }

    public async toggle() {
        const markerArea = await this.getMarkerArea();
        if (markerArea.isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }

    protected _dispose(): void {
        super._dispose();

        const markerArea = this._markerArea$.value;
        if (markerArea && markerArea.isOpen) {
            markerArea.close();
        }
    }

    private _maybeInit(): void {
        if (this._markerAreaConstructionStarted) {
            return;
        }
        this._markerAreaConstructionStarted = true;

        // Create layer canvas
        if (!this._container) {
            this._container = this._root.container.children.push(am5.Container.new(this._root, {
                paddingTop: this.plotTop,
                paddingLeft: this.plotLeft,
                width: am5.percent((this.containerWidth - this.plotRight) / this.containerWidth * 100),
                height: am5.percent(this.plotBottom / this.containerHeight * 100),
                layer: this.get('layer'),
                interactiveChildren: false
            }));

            this._picture = this._container.children.push(am5.Picture.new(this._root, {
                width: am5.p100,
                height: am5.p100
            }));
        }

        // Create MarkerArea
        this._getMarkerJS().then(markerjs2 => {
            if (this.isDisposed()) {
                return;
            }
            this._container._display.setLayer(
                1000,
                {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                }
            );
            const canvas = this._container._display.getCanvas();
            const markerArea = new markerjs2.MarkerArea(canvas);
            try {
                markerArea.uiStyleSettings.logoPosition = 'right';
                markerArea.uiStyleSettings.zIndex = 20;
                markerArea.targetRoot = canvas.parentElement ?? document.body;
                markerArea.availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);

                // Apply custom settings
                const markerSettings = this.get('markerSettings', {});
                $object.each(markerSettings, (key, value) => {
                    markerArea.settings[key] = value;
                });

                const markerStyleSettings = this.get('markerStyleSettings', {});
                $object.each(markerStyleSettings, (key, value) => {
                    markerArea.uiStyleSettings[key] = value;
                });
            } catch (e) {
                this._markerAreaConstructionStarted = false;
                return;
            }

            this._disposers.push($utils.addEventListener(markerArea, 'close', () => {
                this._root._renderer.interactionsEnabled = true;
                this._picture!.show(0);
                this._markerArea$.next(null);
                this._markerAreaConstructionStarted = false;
            }));

            this._disposers.push($utils.addEventListener(markerArea, 'render', (event: any) => {
                const picture = this._picture;
                const finishBake = () => {
                    this._baking = false;
                    this.bakeCompleted$.next();
                    if (this._rebakeNeeded) {
                        this._rebakeNeeded = false;
                        this._renderState();
                    }
                };
                if (picture.get('src') === event.dataUrl) {
                    finishBake();
                } else {
                    picture.events.once('loaded', finishBake);
                    picture.events.once('loaderror', finishBake);
                }
                picture.set('src', event.dataUrl);
                if (!this._skipRender) {
                    this.set('markerState', event.state);
                }
                this._skipRender = false;
            }));

            this._markerArea$.next(markerArea);
        });
    }

    setContainerWidth(width: number): void {
        this.containerWidth = width;
    }

    setContainerHeight(height: number): void {
        this.containerHeight = height;
    }

    setPlotTop(top: number): void {
        this.plotTop = top;
    }

    setPlotBottom(bottom: number): void {
        this.plotBottom = bottom;
    }

    setPlotLeft(left: number): void {
        this.plotLeft = left;
    }

    setPlotRight(right: number): void {
        this.plotRight = right;
    }

    public applyContainerGeometry(): void {
        if (!this._container || !this.containerWidth || !this.containerHeight) {
            return;
        }
        const geometry: [number, number, number, number, number, number] = [
            this.containerWidth, this.containerHeight, this.plotTop, this.plotBottom, this.plotLeft, this.plotRight
        ];
        if (this._appliedGeometry && geometry.every((v, i) => v === this._appliedGeometry[i])) {
            return;
        }
        this._appliedGeometry = geometry;
        this._container.set('paddingTop', this.plotTop);
        this._container.set('paddingLeft', this.plotLeft);
        this._container.set('width', am5.percent((this.containerWidth - this.plotRight) / this.containerWidth * 100));
        this._container.set('height', am5.percent(this.plotBottom / this.containerHeight * 100));
        if (this.get('markerState')) {
            this._renderState();
        }
    }

    /**
     * @ignore
     */
    private async _getMarkerJS(): Promise<any> {
        return await import(/* webpackChunkName: 'markerjs2' */ 'markerjs2');
    }

    /**
     * An instance of MarkerJS's [[MarkerArea]].
     *
     * @see {@link https://markerjs.com/docs/getting-started} for more info
     * @return MarkerArea
     */
    public async getMarkerArea(): Promise<any> {
        this._maybeInit();
        if (this._markerArea$.value) {
            return this._markerArea$.value;
        }
        return firstValueFrom(this._markerArea$.pipe(filter(x => !!x), take(1)));
    }
}
